import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, MapPin, Images, X } from "lucide-react";
import { api, previewUrl, rupiah } from "@/lib/api";
import { PageHeader } from "@/components/Common";

export const KIND_LABEL = { event: "Event", non_event: "Non Event" };

export const KindBadge = ({ kind, className = "", testId }) => (
  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold ${kind === "non_event" ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-700"} ${className}`} data-testid={testId}>
    {KIND_LABEL[kind] || "Event"}
  </span>
);

export const EventCard = ({ event, to }) => (
  <Link to={to || `/events/${event.id}`} className="group block rounded-2xl overflow-hidden bg-white border border-zinc-200 hover:border-zinc-300 hover:-translate-y-0.5 transition-[transform,border-color] duration-300" data-testid={`event-card-${event.id}`}>
    <div className="relative aspect-[16/10] bg-zinc-100 overflow-hidden">
      <KindBadge kind={event.kind} className="absolute top-3 left-3 z-10 shadow-sm" testId={`event-kind-${event.id}`} />
      {event.cover_photo_id ? (
        <img src={previewUrl(event.cover_photo_id)} alt={event.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
      ) : (
        <div className="w-full h-full grid place-items-center text-zinc-400"><Images className="h-8 w-8" /></div>
      )}
    </div>
    <div className="p-5">
      <h3 className="font-bold text-zinc-900 text-lg truncate">{event.title}</h3>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-500">
        {event.date && <span className="inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" />{event.date}</span>}
        {event.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{event.location}</span>}
      </div>
      <div className="mt-4 flex items-center justify-between text-sm">
        <span className="text-zinc-600">{event.photographer_name} · {event.photo_count} foto</span>
        <span className="font-mono-x font-semibold text-zinc-900">{rupiah(event.price)}</span>
      </div>
    </div>
  </Link>
);

const COPY = {
  event: { title: "Semua Event", search: "Cari event, lokasi, fotografer...", empty: "Tidak ada event." },
  non_event: { title: "Foto Non Event", search: "Cari album, lokasi, fotografer...", empty: "Belum ada album non event." },
};

const LocationFilter = ({ kind, value, onChange }) => {
  const [locs, setLocs] = useState([]);
  useEffect(() => { api.get("/events/locations", { params: { kind } }).then((r) => setLocs(r.data)).catch(() => setLocs([])); }, [kind]);
  if (locs.length === 0) return null;
  return (
    <div className="mb-8 -mx-1 px-1 flex gap-2 overflow-x-auto pb-1" data-testid="location-filter">
      <button onClick={() => onChange(null)} data-testid="location-filter-all"
        className={`shrink-0 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${!value ? "bg-zinc-900 border-zinc-900 text-white" : "bg-white border-zinc-200 text-zinc-700 hover:border-zinc-400"}`}>Semua lokasi</button>
      {locs.map((l) => {
        const active = value && value.toLowerCase() === l.location.toLowerCase();
        return (
          <button key={l.location} onClick={() => onChange(active ? null : l.location)} data-testid={`location-filter-${l.location.toLowerCase().replace(/\s+/g, "-")}`}
            className={`shrink-0 inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm transition-colors ${active ? "bg-blue-600 border-blue-600 text-white" : "bg-white border-zinc-200 text-zinc-700 hover:border-zinc-400"}`}>
            <MapPin className="h-3.5 w-3.5" /><span className="font-medium">{l.location}</span>
            <span className={`text-xs ${active ? "text-blue-100" : "text-zinc-400"}`}>{l.count} album</span>
            {active && <X className="h-3.5 w-3.5" />}
          </button>
        );
      })}
    </div>
  );
};

export default function EventsPage({ kind = "event" }) {
  const [events, setEvents] = useState(null);
  const [q, setQ] = useState("");
  const [location, setLocation] = useState(null);
  const copy = COPY[kind];
  useEffect(() => {
    setEvents(null);
    api.get("/events", { params: { kind, ...(location ? { location } : {}) } }).then((r) => setEvents(r.data));
  }, [kind, location]);
  const filtered = (events || []).filter((e) => `${e.title} ${e.location} ${e.photographer_name}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <PageHeader label={kind === "non_event" ? "Galeri · Foto harian & tempat umum" : "Galeri"} title={copy.title}>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={copy.search} data-testid="events-search-input"
          className="h-11 w-full sm:w-80 rounded-xl border border-zinc-200 bg-white px-4 text-sm outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500" />
      </PageHeader>
      <LocationFilter kind={kind} value={location} onChange={setLocation} />
      {events === null ? <p className="text-zinc-500">Memuat...</p> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="events-grid">
          {filtered.map((e) => <EventCard key={e.id} event={e} />)}
          {filtered.length === 0 && <p className="text-zinc-500" data-testid="events-empty">{copy.empty}</p>}
        </div>
      )}
    </main>
  );
}
